import { i18n } from "@tellescope/i18n";
import type { IntegrationName, OrgPlanName } from "@tellescope/tanstack-query";
import { IntegrationConfig } from "../../../../types";
import { Button, DialogFooter, DialogSection } from "@tellescope/ui";
import { useMemo, useState } from "react";
import { getPlanDefaultIntegrations, getPlanIntegrations, getPlanRequiredIntegrations } from "../../../../utils";
import { IntegrationOrbit } from "../../../IntegrationOrbit";
import { IntegrationSelectionList } from "../../../IntegrationSelectionList";

export interface SelectIntegrationsStepProps {
  plan: OrgPlanName;
  initialSelected?: IntegrationName[];
  onSkip?: () => void;
  skipLabel?: string;
  onContinue: (selectedIntegrations: IntegrationName[]) => void;
  continueLabel?: string;
  minimumIntegrationCount?: number;
}

export const SelectIntegrationsStep = ({
  plan,
  initialSelected,
  onSkip,
  skipLabel = i18n.t("I'll do this later", { ns: "onboarding" }),
  onContinue,
  continueLabel = i18n.t("Continue with selected integrations", { ns: "onboarding" }),
  minimumIntegrationCount = 1,
}: SelectIntegrationsStepProps) => {
  const integrationOptions = useMemo<IntegrationConfig[]>(() => getPlanIntegrations({ plan }), [plan]);

  // Seed from any prior selection, falling back to the plan's defaults, and
  // always force-include required integrations (which can't be toggled off).
  const initialSelection = useMemo<IntegrationName[]>(() => {
    const base = initialSelected?.length ? initialSelected : getPlanDefaultIntegrations({ plan });
    return Array.from(new Set([...base, ...getPlanRequiredIntegrations({ plan })]));
  }, [initialSelected, plan]);

  const [selectedIntegrationNames, setSelectedIntegrationNames] = useState<IntegrationName[]>(initialSelection);

  return (
    <>
      <DialogSection className="px-8 grid grid-cols-[1fr_2fr] grid-rows-[minmax(0,1fr)] py-0 bg-gray-50 h-[500px] overflow-hidden">
        <IntegrationSelectionList
          options={integrationOptions}
          selectedIntegrations={selectedIntegrationNames}
          setSelectedIntegrations={setSelectedIntegrationNames}
        />

        <IntegrationOrbit selectedIntegrations={selectedIntegrationNames} />
      </DialogSection>
      <DialogFooter className="bg-gray-50 flex items-center justify-end py-4 px-8">
        {!!onSkip && (
          <Button variant="outline" onClick={onSkip}>
            {skipLabel}
          </Button>
        )}
        <Button
          onClick={() => onContinue(selectedIntegrationNames)}
          disabled={selectedIntegrationNames.length < minimumIntegrationCount}
        >
          {continueLabel}
        </Button>
      </DialogFooter>
    </>
  );
};
